import { useEffect, useRef, useState, type RefObject } from 'react';
import { useMotionValue, type MotionValue } from 'motion/react';

export function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState(() =>
    typeof window !== 'undefined' ? window.matchMedia(query).matches : false
  );

  useEffect(() => {
    const mq = window.matchMedia(query);
    const onChange = () => setMatches(mq.matches);
    onChange();
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, [query]);

  return matches;
}

/** True only while the element is in (or near) the viewport — used to pause marquees. */
export function useInViewOnceNear<T extends HTMLElement = HTMLElement>(
  rootMargin = '20% 0px'
) {
  const ref = useRef<T | null>(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el || typeof IntersectionObserver === 'undefined') {
      setInView(true);
      return;
    }
    const io = new IntersectionObserver(
      ([entry]) => setInView(entry.isIntersecting),
      { rootMargin }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [rootMargin]);

  return { ref, inView };
}

/** Which page section is currently in view — for nav highlighting. */
export function useActiveSection(ids: string[]) {
  const [active, setActive] = useState(ids[0] ?? '');

  useEffect(() => {
    const elements = ids
      .map((id) => document.getElementById(id))
      .filter((el): el is HTMLElement => !!el);
    if (!elements.length) return;

    const ratios = new Map<string, number>();
    const io = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          ratios.set(entry.target.id, entry.isIntersecting ? entry.intersectionRatio : 0);
        }
        let best = ids[0];
        let bestRatio = -1;
        for (const id of ids) {
          const r = ratios.get(id) ?? 0;
          if (r > bestRatio) {
            bestRatio = r;
            best = id;
          }
        }
        // Near top of page → treat as home
        if (window.scrollY < 120) {
          setActive('home');
          return;
        }
        setActive(best);
      },
      { threshold: [0.15, 0.35, 0.55, 0.75], rootMargin: '-20% 0px -45% 0px' }
    );

    elements.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, [ids]);

  return active;
}

/** Viewport scrollY after paint — avoids Motion useScroll's layout-effect read. */
export function useWindowScrollY(): MotionValue<number> {
  const y = useMotionValue(typeof window === 'undefined' ? 0 : window.scrollY);

  useEffect(() => {
    let raf = 0;
    const flush = () => {
      raf = 0;
      y.set(window.scrollY);
    };
    const onScroll = () => {
      if (!raf) raf = requestAnimationFrame(flush);
    };
    flush();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, [y]);

  return y;
}

/** Section scroll progress 0–1, measured after paint (not in useLayoutEffect). */
export function useSectionScrollProgress(ref: RefObject<HTMLElement | null>): MotionValue<number> {
  const progress = useMotionValue(0);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    let start = 0;
    let range = 1;
    let raf = 0;

    const measure = () => {
      start = el.getBoundingClientRect().top + window.scrollY;
      range = Math.max(el.offsetHeight - window.innerHeight, 1);
    };

    const flush = () => {
      raf = 0;
      progress.set(Math.min(1, Math.max(0, (window.scrollY - start) / range)));
    };

    const onScroll = () => {
      if (!raf) raf = requestAnimationFrame(flush);
    };

    const onResize = () => {
      measure();
      flush();
    };

    measure();
    flush();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onResize, { passive: true });
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(onResize) : null;
    ro?.observe(el);

    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onResize);
      ro?.disconnect();
      if (raf) cancelAnimationFrame(raf);
    };
  }, [ref, progress]);

  return progress;
}
